import React, { useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import SingleSelect from 'components/singleSelect/SingleSelect';
import { toast } from 'react-toastify';
import { api } from 'api';
import './_offlineBooking.scss';
import '../bookings/_bookings.scss';
import '../slots/_fieldCreate.scss';
import { useDispatch, useSelector } from "react-redux";
import { generalExtraActions } from "../../reduxStore/actions";
import { Loader } from 'components';
import { formatPrice } from 'common/utils';

// Helper to get weekday string from date
function getWeekday(dateStr) {
  if (!dateStr) return null;
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const d = new Date(dateStr);
  return days[d.getDay()];
}

// Helper to get today's date in YYYY-MM-DD format according to user's timezone
function getTodayString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Helper to format time in 12-hour format
function formatTime12Hour(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

// Helper to normalize time format for comparison (removes seconds if present)
function normalizeTime(time) {
  if (!time) return '';
  // Remove seconds if present (e.g., "13:32:00" -> "13:32")
  return time.split(':').slice(0, 2).join(':');
}

function OfflineBookingCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sports,
  } = useSelector((state) => state.general);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedGround, setSelectedGround] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDates, setSelectedDates] = useState([]); // Multi-date selection
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [dateSlotSelections, setDateSlotSelections] = useState({}); // {date: [slotIds]}
  const [players, setPlayers] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [fields, setFields] = useState([]);
  const [slots, setSlots] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [groundsLoading, setGroundsLoading] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [groundSearchQuery, setGroundSearchQuery] = useState('');
  const [isPrePopulating, setIsPrePopulating] = useState(false);
  const formikRef = React.useRef(null);
  const slotMatchedRef = React.useRef(false);

  useEffect(() => {
    dispatch(generalExtraActions.getSports());
    fetchPlayers();
    fetchGrounds();
  }, [dispatch]);

  // Pre-populate form from calendar navigation state
  useEffect(() => {
    const prePopulateFromState = async () => {
      const state = location.state;
      if (!state || !state.field || !state.sport || !state.date) {
        return; // No state or incomplete state (field, sport, date are required), skip pre-population
      }

      // Wait for sports to be loaded if they're not available yet
      if (!sports || sports.length === 0) {
        return;
      }

      setIsPrePopulating(true);
      try {
        // Fetch field detail first (we need this to get the ground if it's missing)
        const fieldData = await api.getFieldDetail(state.field);
        setSelectedField(fieldData);
        
        // Get ground ID from state or from field data
        let groundId = state.ground;
        if (!groundId && fieldData) {
          // Try to get ground from field data
          if (typeof fieldData.ground === 'object' && fieldData.ground?.id) {
            groundId = fieldData.ground.id;
          } else if (typeof fieldData.ground === 'number' || typeof fieldData.ground === 'string') {
            groundId = fieldData.ground;
          }
        }

        // If we have a ground ID, fetch ground detail
        if (groundId) {
          const groundData = await api.getGroundDetail(groundId);
          setSelectedGround(groundData);
          
          // Fetch fields for the ground (needed for form validation)
          const fieldsResponse = await api.getFields({ ground: groundId });
          setFields(fieldsResponse?.results || []);
        } else {
          // If ground is still missing, try to fetch fields without ground filter
          // This might happen if the field data doesn't have ground info
          console.warn('Ground ID not found in state or field data');
        }
        
        // Set sport - handle both ID and object formats
        const sportId = typeof state.sport === 'object' ? state.sport.id : state.sport;
        const sportObj = sports?.find(s => s.id === sportId);
        if (sportObj) {
          setSelectedSport(sportObj);
        }

        // Set date
        setSelectedDate(state.date);
        setSelectedDates([state.date]);

        // Fetch slots with bookings for the field and date
        if (state.start_time && state.end_time) {
          await fetchFieldWithBookings(state.field, state.date);
          
          // After slots are loaded, find and select the matching slot
          // This will be handled in a separate useEffect that watches slots
        }

        // Update form values after pre-population
        if (formikRef.current) {
          if (groundId) {
            formikRef.current.setFieldValue('ground', groundId);
          }
          const sportIdValue = typeof state.sport === 'object' ? state.sport.id : state.sport;
          formikRef.current.setFieldValue('sport', sportIdValue);
          formikRef.current.setFieldValue('field', state.field);
          formikRef.current.setFieldValue('date', state.date);
        }
      } catch (error) {
        console.error('Error pre-populating form:', error);
        toast.error('Failed to load booking details');
      } finally {
        setIsPrePopulating(false);
      }
    };

    prePopulateFromState();
  }, [location.state, sports]);

  // Find and select the matching slot when slots are loaded and we have state
  useEffect(() => {
    const state = location.state;
    if (!state || !state.start_time || !state.end_time || !slots.length || isPrePopulating) {
      return;
    }

    // Only try to match once per navigation
    if (slotMatchedRef.current) {
      return;
    }

    // Normalize times for comparison (handle both "HH:mm" and "HH:mm:ss" formats)
    const normalizedStartTime = normalizeTime(state.start_time);
    const normalizedEndTime = normalizeTime(state.end_time);
    
    // Get the weekday for the selected date
    const weekday = getWeekday(state.date);

    // Find the slot matching the start_time, end_time, and day
    const matchingSlot = slots.find(slot => {
      const slotStartTime = normalizeTime(slot.start_time);
      const slotEndTime = normalizeTime(slot.end_time);
      const timeMatches = slotStartTime === normalizedStartTime && slotEndTime === normalizedEndTime;
      // Also check day if available
      const dayMatches = !slot.day || slot.day === weekday;
      return timeMatches && dayMatches;
    });

    if (matchingSlot && !matchingSlot.is_booked) {
      // Select the matching slot
      const date = state.date;
      setDateSlotSelections(prev => ({
        ...prev,
        [date]: [matchingSlot.id]
      }));
      setSelectedSlots([matchingSlot]);
      
      // Ensure the date is in selectedDates
      setSelectedDates(prev => {
        if (!prev.includes(date)) {
          return [...prev, date].sort();
        }
        return prev;
      });
      
      // Update form value
      if (formikRef.current) {
        formikRef.current.setFieldValue('slots', [matchingSlot.id]);
      }
      
      // Mark as matched to prevent re-matching
      slotMatchedRef.current = true;
    } else if (matchingSlot && matchingSlot.is_booked) {
      console.warn('Matching slot found but it is already booked');
      toast.warn('The selected slot is already booked. Please select a different slot.');
      slotMatchedRef.current = true; // Mark as processed even if booked
    } else {
      console.warn('No matching slot found', {
        stateTimes: { start: normalizedStartTime, end: normalizedEndTime },
        stateDate: state.date,
        weekday: weekday,
        availableSlots: slots.map(s => ({
          id: s.id,
          start: normalizeTime(s.start_time),
          end: normalizeTime(s.end_time),
          day: s.day,
          is_booked: s.is_booked
        }))
      });
      toast.warn('Could not find the matching slot. Please select a slot manually.');
      slotMatchedRef.current = true; // Mark as processed to prevent repeated warnings
    }
  }, [slots, location.state, isPrePopulating]);

  // Reset slot matched ref when location.state changes (new navigation)
  useEffect(() => {
    slotMatchedRef.current = false;
  }, [location.state]);

  // Fetch players with search
  const fetchPlayers = async (searchQuery = '') => {
    setPlayersLoading(true);
    try {
      const response = await api.searchPlayers(searchQuery);
      setPlayers(response || []);
    } catch (error) {
      toast.error('Failed to load players');
    } finally {
      setPlayersLoading(false);
    }
  };

  // Fetch grounds with search
  const fetchGrounds = async (searchQuery = '') => {
    setGroundsLoading(true);
    try {
      const response = await api.searchGrounds(searchQuery);
      setGrounds(response || []);
    } catch (error) {
      toast.error('Failed to load grounds');
    } finally {
      setGroundsLoading(false);
    }
  };

  // Debounced search for players
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPlayers(playerSearchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [playerSearchQuery]);

  // Debounced search for grounds
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchGrounds(groundSearchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [groundSearchQuery]);

  // Fetch field detail with booking information when field and date are selected
  const fetchFieldWithBookings = async (fieldId, date) => {
    if (!fieldId || !date) {
      setSlots([]);
      setExistingBookings([]);
      return;
    }

    setBookingsLoading(true);
    try {
      // Fetch field detail with booking information
      const fieldData = await api.getFieldDetailWithBookings(fieldId, date);

      if (fieldData && fieldData.field_slots) {
        // Convert field_slots object to flat array with booking information
        const allSlots = [];
        Object.keys(fieldData.field_slots).forEach(day => {
          const daySlots = fieldData.field_slots[day];
          // Add day property and booking status to each slot
          const slotsWithDay = daySlots.map(slot => ({
            ...slot,
            day: day,
            is_booked: slot.is_booked || false // Ensure is_booked is always a boolean
          }));
          allSlots.push(...slotsWithDay);
        });

        // Debug log to see slot booking status
        console.log('Slots with booking status:', allSlots.map(slot => ({
          id: slot.id,
          time: `${slot.start_time}-${slot.end_time}`,
          is_booked: slot.is_booked
        })));

        setSlots(allSlots);
      }

      // Also fetch existing bookings for additional context
      if (fieldData && fieldData.existing_bookings) {
        setExistingBookings(fieldData.existing_bookings);
      } else {
        setExistingBookings([]);
      }
    } catch (error) {
      console.error('Failed to fetch field with bookings:', error);
      setSlots([]);
      setExistingBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  // Fetch fields when ground is selected
  useEffect(() => {
    const fetchFields = async () => {
      if (selectedGround) {
        try {
          const response = await api.getFields({ ground: selectedGround.id });
          setFields(response?.results || []);
        } catch (error) {
          toast.error('Failed to load fields');
        }
      } else {
        setFields([]);
      }
    };
    fetchFields();
  }, [selectedGround]);

  // Fetch slots when field is selected (without date)
  useEffect(() => {
    const fetchSlots = async () => {
      if (selectedField) {
        try {
          const fieldData = await api.getFieldDetail(selectedField.id);
          if (fieldData && fieldData.field_slots) {
            // Convert field_slots object to flat array
            const allSlots = [];
            Object.keys(fieldData.field_slots).forEach(day => {
              const daySlots = fieldData.field_slots[day];
              // Add day property and default booking status to each slot
              const slotsWithDay = daySlots.map(slot => ({
                ...slot,
                day: day,
                is_booked: false // Default to not booked when no date is selected
              }));
              allSlots.push(...slotsWithDay);
            });
            setSlots(allSlots);
          }
        } catch (error) {
          toast.error('Failed to load slots');
        }
      } else {
        setSlots([]);
      }
    };
    fetchSlots();
  }, [selectedField]);

  const getSelectedSlotObjects = (ids) => {
    if (!slots.length) return [];
    return slots.filter(s => ids.includes(s.id));
  };

  const totalPrice = (() => {
    // Always use multiple date mode: sum slots for all dates that have slots selected
    const allDatesWithSlots = Object.keys(dateSlotSelections).filter(date =>
      dateSlotSelections[date] && dateSlotSelections[date].length > 0
    );

    return allDatesWithSlots.reduce((total, date) => {
      const slotsForDate = dateSlotSelections[date] || [];
      const slotObjects = getSelectedSlotObjects(slotsForDate);
      return total + slotObjects.reduce((sum, slot) => sum + slot.price, 0);
    }, 0);
  })();
  const playerOptions = players?.map(p => ({label: `${p.full_name} (${p.email})`, value: p.id}));

  // Filter sports to show only those supported by the selected ground
  const sportOptions = (() => {
    if (!selectedGround) return [];
    
    // If ground has sports array, filter sports based on ground's supported sports
    if (selectedGround.sports && Array.isArray(selectedGround.sports)) {
      const groundSportIds = selectedGround.sports.map(sport => 
        typeof sport === 'object' ? sport.id : sport
      );
      return sports
        ?.filter(sport => groundSportIds.includes(sport.id))
        .map(s => ({label: s.name, value: s.id})) || [];
    }
    
    // If no sports info on ground, return empty (shouldn't happen, but safe fallback)
    return [];
  })();

  // Filter fields to show only those that belong to selected ground AND support selected sport
  const fieldOptions = fields?.filter(field => {
    // First, ensure field belongs to the selected ground
    if (selectedGround) {
      const fieldGroundId = typeof field.ground === 'object' ? field.ground?.id : field.ground;
      if (fieldGroundId !== selectedGround.id) {
        return false;
      }
    }

    // If no sport is selected, show all fields for the selected ground
    if (!selectedSport) return true;

    // Check if the field supports the selected sport
    // Assuming field has a sports array or supported_sports field
    if (field.sports && Array.isArray(field.sports)) {
      return field.sports.some(sport => {
        const sportId = typeof sport === 'object' ? sport.id : sport;
        return sportId === selectedSport.id;
      });
    }

    // If field has supported_sports field
    if (field.supported_sports && Array.isArray(field.supported_sports)) {
      return field.supported_sports.some(sport => {
        const sportId = typeof sport === 'object' ? sport.id : sport;
        return sportId === selectedSport.id;
      });
    }

    // If field has sport field (single sport)
    if (field.sport) {
      const sportId = typeof field.sport === 'object' ? field.sport.id : field.sport;
      return sportId === selectedSport.id;
    }

    // If no sport information is available, don't show the field (strict filtering)
    return false;
  }).map(f => ({label: f.name, value: f.id}));

  // Slot selection handler
  const handleSlotClick = (slot, values, setFieldValue) => {
    const currentDate = values.date;

    if (!currentDate) return; // Don't allow slot selection without a date
    if (slot.is_booked) return; // Don't allow selection of booked slots

    // Get current slots for this date
    const currentSlotsForDate = dateSlotSelections[currentDate] || [];

    let newSelected;
    if (currentSlotsForDate.includes(slot.id)) {
      newSelected = currentSlotsForDate.filter(id => id !== slot.id);
    } else {
      newSelected = [...currentSlotsForDate, slot.id];
    }

    // Update date-slot selections
    setDateSlotSelections(prev => ({
      ...prev,
      [currentDate]: newSelected
    }));

    // Update selectedDates: add date if slots are selected, remove if no slots
    setSelectedDates(prev => {
      if (newSelected.length > 0) {
        // Add date to selectedDates if it's not already there
        if (!prev.includes(currentDate)) {
          return [...prev, currentDate].sort();
        }
        return prev;
      } else {
        // Remove date from selectedDates if no slots are selected
        return prev.filter(d => d !== currentDate);
      }
    });

    // Update form field for current date
    setFieldValue('slots', newSelected);
    setSelectedSlots(getSelectedSlotObjects(newSelected));
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      // Prepare dates_slots data - get all dates that have slots selected
      const datesSlotsData = Object.keys(dateSlotSelections)
        .filter(date => dateSlotSelections[date] && dateSlotSelections[date].length > 0)
        .map(date => ({
          date: date,
          slots: dateSlotSelections[date]
        }));

      const bookingData = {
        ground_field: selectedField.id,
        sport: values.sport || null,
        dates_slots: datesSlotsData,
        payment_method: 'pay_at_ground',
        user: values.player || null, // Player is optional
        status: 'confirmed', // Ground admin creates confirmed bookings
      };

      // Debug log to verify what's being sent
      console.log('Sending booking data:', bookingData);
      console.log('Dates with slots:', datesSlotsData);

      await api.createOfflineBooking(bookingData);
      toast.success('Offline booking created and confirmed successfully!');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to create offline booking');
      console.error('Booking creation error:', error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  console.log(location?.state)

  return (
    <div className="field-create-page ground-detail-page detail-page">
      {loading && <Loader />}
      <div className="header">
        <h1>Create Offline Booking</h1>
        <p className="subtitle">Book a ground field (offline) - Booking will be confirmed automatically. Player selection is optional.</p>
      </div>
      <Formik
        innerRef={formikRef}
        initialValues={{
          player: '',
          ground: location.state?.ground || '',
          sport: location.state?.sport || '',
          field: location.state?.field || '',
          date: location.state?.date || '',
          slots: []
        }}
        enableReinitialize={true}
        validate={values => {
          const errors = {};
          // Player is optional for offline bookings
          if (!values.ground) errors.ground = 'Ground is required';
          if (!values.sport) errors.sport = 'Sport is required';
          if (!values.field) errors.field = 'Field is required';
          if (!values.date) errors.date = 'Date is required';
          if (!values.slots || values.slots.length === 0) errors.slots = 'At least one slot must be selected';
          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {({values, setFieldValue}) => {
          // Reset dependent fields when parent changes
          const handlePlayerChange = (val) => {
            setFieldValue('player', val);
            const player = players.find(p => p.id === val);
            setSelectedPlayer(player);
          };

          const handleGroundChange = (val) => {
            setFieldValue('ground', val);
            setSelectedGround(grounds.find(g => g.id === val));
            setFieldValue('sport', '');
            setSelectedSport(null);
            setFieldValue('field', '');
            setSelectedField(null);
            setFieldValue('slots', []);
            setSelectedSlots([]);
            setDateSlotSelections({}); // Clear all date-slot selections when ground changes
            setSelectedDates([]); // Clear selected dates when ground changes
          };

          const handleSportChange = (val) => {
            setFieldValue('sport', val);
            setSelectedSport(sports.find(s => s.id === val));
            setFieldValue('field', '');
            setSelectedField(null);
            setFieldValue('slots', []);
            setSelectedSlots([]);
            setDateSlotSelections({}); // Clear all date-slot selections when sport changes
            setSelectedDates([]); // Clear selected dates when sport changes
          };

          const handleFieldChange = (val) => {
            setFieldValue('field', val);
            const field = fields.find(f => f.id === val);
            setSelectedField(field);
            setFieldValue('slots', []);
            setSelectedSlots([]);
            setDateSlotSelections({}); // Clear all date-slot selections when field changes
            setSelectedDates([]); // Clear selected dates when field changes
            // Fetch field with booking information if date is selected, otherwise basic field details
            if (field && selectedDate) {
              fetchFieldWithBookings(field.id, selectedDate);
            }
            // If no date selected, the useEffect will handle fetching basic field details
          };

          const handleDateChange = (e) => {
            const dateValue = e.target.value;
            setFieldValue('date', dateValue);
            setSelectedDate(dateValue);

            // Don't automatically add date to selectedDates - only add when slots are selected
            // Just set the current date for slot selection

            // Load slots for this date if they exist in dateSlotSelections
            if (dateSlotSelections[dateValue]) {
              setFieldValue('slots', dateSlotSelections[dateValue]);
              setSelectedSlots(getSelectedSlotObjects(dateSlotSelections[dateValue]));
            } else {
              // Clear slots if no previous selection for this date
              setFieldValue('slots', []);
              setSelectedSlots([]);
            }

            // Fetch field with booking information for the selected field and date
            if (selectedField && dateValue) {
              fetchFieldWithBookings(selectedField.id, dateValue);
            }
          };

          // Filter slots by selected day and check if time has passed
          const getAvailableSlots = () => {
            if (!selectedDate || !slots.length) return [];
            const weekday = getWeekday(selectedDate);
            const dateObj = new Date(selectedDate);
            const today = new Date();
            const isToday = dateObj.toDateString() === today.toDateString();

            return slots.filter(slot => {
              // Filter by day
              if (slot.day !== weekday) return false;

              // If it's today, check if the slot time has passed
              if (isToday) {
                const now = new Date();
                const [hours, minutes] = slot.start_time.split(':');
                const slotStartTime = new Date();
                slotStartTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                // Only show slots that haven't started yet (with 15 minute buffer)
                const bufferTime = new Date(now.getTime() + 15 * 60000); // 15 minutes buffer
                return slotStartTime > bufferTime;
              }

              // For future dates, show all slots (booked slots will be disabled in UI)
              return true;
            });
          };

          const availableSlots = getAvailableSlots();

          return (
            <Form className="custom-form">
              {/* Player Selection Section */}
              <div className="basic-info-content">
                <h3>Step 1: Select Player (Optional)</h3>
                <div className="form-group">
                  <label htmlFor="player">Player</label>
                  <SingleSelect
                    options={playerOptions || []}
                    value={values.player}
                    onChange={handlePlayerChange}
                    placeholder="Search and select a player (optional)"
                    isLoading={playersLoading}
                    onSearchChange={setPlayerSearchQuery}
                    searchable={true}
                  />
                  <ErrorMessage name="player" component="div" className="error-message"/>
                </div>
              </div>

              {/* Ground Selection Section */}
              <div className="basic-info-content">
                <h3>Step 2: Select Ground</h3>
                <div className="form-group">
                  <label htmlFor="ground">Ground <span className="required">*</span></label>
                  <SingleSelect
                    options={grounds.map(g => ({label: g.name, value: g.id}))}
                    value={values.ground}
                    onChange={handleGroundChange}
                    placeholder="Search and select a ground"
                    isLoading={groundsLoading}
                    onSearchChange={setGroundSearchQuery}
                    searchable={true}
                  />
                  <ErrorMessage name="ground" component="div" className="error-message"/>
                </div>
              </div>

              {/* Sport Selection Section */}
              <div className="basic-info-content">
                <h3>Step 3: Select Sport</h3>
                <div className="form-group">
                  <label htmlFor="sport">Sport <span className="required">*</span></label>
                  <SingleSelect
                    options={sportOptions || []}
                    value={values.sport}
                    onChange={handleSportChange}
                    placeholder={selectedGround ?
                      (sportOptions && sportOptions.length > 0 ?
                        "Select a sport" :
                        "No sports available for this ground") :
                      "Select a ground first"}
                    isDisabled={!selectedGround}
                    searchable={false}
                  />
                  {selectedGround && sportOptions && sportOptions.length === 0 && (
                    <div style={{color: '#e74c3c', fontSize: '12px', marginTop: '4px'}}>
                      No sports are available for the selected ground "{selectedGround.name}".
                    </div>
                  )}
                  <ErrorMessage name="sport" component="div" className="error-message"/>
                </div>
              </div>

              {/* Field Selection Section */}
              <div className="basic-info-content">
                <h3>Step 4: Select Field</h3>
                <div className="form-group">
                  <label htmlFor="field">Field <span className="required">*</span></label>
                  <SingleSelect
                    options={fieldOptions || []}
                    value={values.field}
                    onChange={handleFieldChange}
                    placeholder={
                      !selectedGround ? "Select a ground first" :
                      !selectedSport ? "Select a sport first" :
                      (fieldOptions && fieldOptions.length > 0 ?
                        "Select a field" :
                        "No fields available for this sport")
                    }
                    isDisabled={!selectedGround || !selectedSport}
                    searchable={false}
                  />
                  {selectedGround && selectedSport && fieldOptions && fieldOptions.length === 0 && (
                    <div style={{color: '#e74c3c', fontSize: '12px', marginTop: '4px'}}>
                      No fields are available for the selected sport "{selectedSport.name}" at "{selectedGround.name}".
                    </div>
                  )}
                  <ErrorMessage name="field" component="div" className="error-message"/>
                </div>
              </div>

              {/* Date Selection Section */}
              <div className="basic-info-content">
                <h3>Step 5: Select Dates</h3>
                <div className="form-group">
                  <label htmlFor="date">Add Date <span className="required">*</span></label>
                  <Field
                    type="date"
                    name="date"
                    className="form-control"
                    onChange={handleDateChange}
                    min={getTodayString()}
                  />
                  <ErrorMessage name="date" component="div" className="error-message"/>

                  {selectedDates.length > 0 && (
                    <div style={{marginTop: '12px'}}>
                      <div style={{fontSize: '14px', color: '#666', marginBottom: '8px', fontWeight: '500'}}>
                        Selected Dates ({selectedDates.length}):
                      </div>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                        {selectedDates.map(date => {
                          const isActiveDate = date === values.date;
                          return (
                          <span
                            key={date}
                            onClick={() => {
                              // Switch to this date to view/edit its slots
                              setFieldValue('date', date);
                              setSelectedDate(date);
                              if (dateSlotSelections[date]) {
                                setFieldValue('slots', dateSlotSelections[date]);
                                setSelectedSlots(getSelectedSlotObjects(dateSlotSelections[date]));
                              } else {
                                setFieldValue('slots', []);
                                setSelectedSlots([]);
                              }
                              // Fetch field with booking information for this date
                              if (selectedField && date) {
                                fetchFieldWithBookings(selectedField.id, date);
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: isActiveDate ? '#0BAB7C' : 'white',
                              color: isActiveDate ? 'white' : 'black',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: isActiveDate ? '600' : '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              border: '2px solid #0BAB7C',
                              boxShadow: isActiveDate ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {new Date(date).toLocaleDateString()}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering parent span's onClick
                                // Remove this date and its slots
                                setSelectedDates(prev => prev.filter(d => d !== date));
                                setDateSlotSelections(prev => {
                                  const newSelections = { ...prev };
                                  delete newSelections[date];
                                  return newSelections;
                                });
                                // If this was the current date, clear the form
                                if (date === values.date) {
                                  setFieldValue('date', '');
                                  setSelectedDate('');
                                  setFieldValue('slots', []);
                                  setSelectedSlots([]);
                                }
                              }}
                              style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                color: 'black',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              ×
                            </button>
                          </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Time Slots Selection Section */}
              <div className="basic-info-content">
                <h3>Step 6: Select Time Slots</h3>
                <div className="form-group">
                  <label>Available Slots <span className="required">*</span></label>
                  <div className="checkbox-container">
                    {bookingsLoading && (
                      <div style={{color: '#888', padding: '8px 0'}}>
                        Loading slots and checking availability...
                      </div>
                    )}
                    {!bookingsLoading && availableSlots.length === 0 && (
                      <div style={{color: '#888', padding: '8px 0'}}>
                        {!selectedDate ? 'Select a date to see available slots' :
                         !selectedField ? 'Select a field to see available slots' :
                         selectedDate === getTodayString() ? 'No available slots for today (past slots are hidden)' :
                         'No slots available for the selected day'}
                      </div>
                    )}
                    {!bookingsLoading && availableSlots.map(slot => {
                      const isSelected = values.slots.includes(slot.id);
                      const isBooked = slot.is_booked || false;
                      const isUnderMaintenance = slot.is_under_maintenance || false;
                      const isDisabled = isBooked || isUnderMaintenance; // Disable if slot is booked or under maintenance
                      return (
                        <div
                          key={slot.id}
                          className={`checkbox-item-wrapper ${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                          style={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.6 : 1,
                            backgroundColor: isDisabled ? '#f5f5f5' : 'transparent'
                          }}
                          onClick={() => !isDisabled && handleSlotClick(slot, values, setFieldValue)}
                        >
                          <div className="checkbox-item">
                              <span className="slot-label" style={{color: isDisabled ? '#999' : 'inherit'}}>
                                {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)} (Rs. {formatPrice(slot.price)})
                                {isBooked && <span style={{color: '#c0392b', marginLeft: 8, fontWeight: 'bold'}}>(Booked)</span>}
                                {isUnderMaintenance && <span style={{color: '#f39c12', marginLeft: 8, fontWeight: 'bold'}}>(Maintenance)</span>}
                              </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <ErrorMessage name="slots" component="div" className="error-message"/>
                </div>
              </div>

              {/* Booking Summary Section */}
              {(selectedPlayer || selectedGround || selectedSport || selectedField || values.date || selectedSlots.length > 0 || Object.keys(dateSlotSelections).some(date => dateSlotSelections[date] && dateSlotSelections[date].length > 0)) && (
                <div className="basic-info-content booking-summary">
                  <h3>Booking Summary</h3>
                  <div className="summary-card">
                    <div className="summary-row">
                      <span className="summary-label">Player:</span>
                      <span className="summary-value">{selectedPlayer?.full_name || 'Not selected'}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Ground:</span>
                      <span className="summary-value">{selectedGround?.name || 'Not selected'}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Sport:</span>
                      <span className="summary-value">{selectedSport?.name || 'Not selected'}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Field:</span>
                      <span className="summary-value">{selectedField?.name || 'Not selected'}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Booking Details:</span>
                      <span className="summary-value">
                        {(() => {
                          // Get all dates that have slots selected
                          const allDatesWithSlots = Object.keys(dateSlotSelections).filter(date =>
                            dateSlotSelections[date] && dateSlotSelections[date].length > 0
                          );

                          if (allDatesWithSlots.length === 0) {
                            return 'No slots selected';
                          }

                          return (
                            <div className="slots-list">
                              {allDatesWithSlots.map((date, dateIndex) => {
                                const slotsForDate = dateSlotSelections[date] || [];
                                const slotObjects = getSelectedSlotObjects(slotsForDate);

                                return (
                                  <div key={dateIndex} className="date-slot-group">
                                    <div className="date-header">
                                      {new Date(date).toLocaleDateString()}
                                    </div>
                                    <div className="slots-for-date">
                                      {slotObjects.map((slot, slotIndex) => (
                                        <div key={slotIndex} className="slot-item">
                                          {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)} (Rs. {formatPrice(slot.price)})
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </span>
                    </div>
                    <div className="summary-row total-row">
                      <span className="summary-label">Total Price:</span>
                      <span className="summary-value total-price">Rs. {formatPrice(totalPrice)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions Section */}
              <div className="actions-section">
                <button className="submit-btn primary" type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Booking'}
                </button>
                <button className="submit-btn secondary" type="button" onClick={() => navigate(-1)}>Cancel</button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default OfflineBookingCreate;